/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { VERIFY_STATUS } from './mavenParse';

// Only problems get a badge. A verified answer, or one still being checked, shows nothing.
const BADGES = {
  [VERIFY_STATUS.PARTLY_VERIFIED]: {
    color: 'warning',
    icon: <WarningAmberIcon />,
    label: 'Some numbers were not found in the data'
  },
  [VERIFY_STATUS.NOT_VERIFIED]: {
    color: 'error',
    icon: <ErrorOutlineIcon />,
    label: 'Not verified: this answer did not come from the data'
  }
};

const VerifyBadge = ({ check }) => {
  if (!check || check.loading) return null;
  if (check.error) return <Chip size="small" label="Could not verify this answer" variant="outlined" />;
  const badge = BADGES[check.result?.status];
  if (!badge) return null;
  const missing = check.result.numbers?.missing || [];
  return (
    <Box>
      <Chip size="small" color={badge.color} icon={badge.icon} label={badge.label} data-testid="maven-badge" />
      {check.result.status === VERIFY_STATUS.PARTLY_VERIFIED && missing.length > 0 && (
        <Typography variant="caption" component="p" sx={{ mt: 0.5, opacity: 0.8 }}>
          Not in the query results: {missing.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

VerifyBadge.propTypes = {
  check: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.bool,
    result: PropTypes.shape({ status: PropTypes.string, numbers: PropTypes.shape({ missing: PropTypes.array }) })
  })
};

VerifyBadge.defaultProps = { check: null };

export default VerifyBadge;
